import { dist } from '../utils/math.js';

const MERGE_RADIUS_FACTOR = 0.08;

/**
 * Expanding neon ripples with merge / cancel interactions.
 */
export default class WaveSystem {
  constructor() {
    this.waves = [];
    this._id = 1;
  }

  reset() {
    this.waves.length = 0;
    this._id = 1;
  }

  /**
   * @param {{ x: number, y: number, quality: 'perfect'|'good', combo: number, color?: string }} opts
   */
  spawn(opts) {
    const intensity = opts.quality === 'perfect' ? 1 : 0.72;
    const comboBoost = Math.min(2.2, 1 + opts.combo * 0.03);
    this.waves.push({
      id: this._id++,
      x: opts.x,
      y: opts.y,
      r: 0.01,
      maxR: 0.42 * comboBoost,
      speed: 0.38 * intensity * comboBoost,
      life: 1,
      decay: 0.55 / intensity,
      quality: opts.quality,
      hue: opts.hue ?? 185,
      trail: [],
      merged: false,
      cancel: false
    });
  }

  /**
   * @returns {{ merges: Array, cancels: number }}
   */
  update(dt) {
    const merges = [];
    let cancels = 0;

    for (const w of this.waves) {
      w.trail.push({ x: w.x, y: w.y, r: w.r, a: w.life });
      if (w.trail.length > 8) w.trail.shift();
      w.r += w.speed * dt;
      w.life -= w.decay * dt;
      if (w.r > w.maxR) w.life -= dt * 1.2;
    }

    // pairwise interactions
    for (let i = 0; i < this.waves.length; i++) {
      const a = this.waves[i];
      if (a.life <= 0 || a.merged || a.cancel) continue;
      for (let j = i + 1; j < this.waves.length; j++) {
        const b = this.waves[j];
        if (b.life <= 0 || b.merged || b.cancel) continue;

        const d = dist(a.x, a.y, b.x, b.y);
        const ringDist = Math.abs(a.r - b.r);
        const approaching = Math.abs(d - (a.r + b.r) * 0.5);

        // constructive merge: similar phase, both good+
        if (
          !a.merged &&
          !b.merged &&
          a.quality !== 'miss' &&
          b.quality !== 'miss' &&
          ringDist < MERGE_RADIUS_FACTOR &&
          approaching < MERGE_RADIUS_FACTOR * 1.6 &&
          d < 0.55
        ) {
          a.merged = true;
          b.merged = true;
          const mx = (a.x + b.x) / 2;
          const my = (a.y + b.y) / 2;
          merges.push({ x: mx, y: my, hue: (a.hue + b.hue) / 2 });
          // spawn amplified child
          this.waves.push({
            id: this._id++,
            x: mx,
            y: my,
            r: Math.max(a.r, b.r) * 0.6,
            maxR: 0.55,
            speed: 0.5,
            life: 1,
            decay: 0.4,
            quality: 'perfect',
            hue: (a.hue + b.hue) / 2,
            trail: [],
            merged: false,
            cancel: false,
            amplify: true
          });
          a.life *= 0.35;
          b.life *= 0.35;
        } else if (
          // destructive: opposite-ish timing / far phase mismatch
          Math.abs(a.r - b.r) > 0.22 &&
          d < 0.12 &&
          a.life > 0.4 &&
          b.life > 0.4
        ) {
          a.cancel = true;
          b.cancel = true;
          a.life = Math.min(a.life, 0.2);
          b.life = Math.min(b.life, 0.2);
          cancels++;
        }
      }
    }

    this.waves = this.waves.filter((w) => w.life > 0.02 && w.r < w.maxR * 1.15);

    return { merges, cancels };
  }

  getActive() {
    return this.waves;
  }
}
